import type { QueryResult } from '@apollo/client';
import { ApolloError, useLazyQuery, useQuery } from '@apollo/client';
import classNames from 'classnames';
import * as _ from 'lodash-es';
import type { Channel } from 'phoenix';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useTransition,
} from 'react';
import { useLocation, useSearchParams } from 'react-router';
import attachAiAsk from './AiAsk';
import AiAskAnswer from './AiAskAnswer';
import getSearchResultType, {
  filterBySearchResultType,
} from './getSearchResultType';
import SearchManagePlatformButton from './SearchManagePlatformButton';
import * as styles from './SearchView.module.scss';
import SearchViewExternalResults from './SearchViewExternalResults';
import SearchViewFilterMenu from './SearchViewFilterMenu';
import SearchViewHeader from './SearchViewHeader';
import SearchViewSlabSearch from './SearchViewSlabSearch';
import useIsAiAskEnabled from './useIsAiAskEnabled';
import PageLayout from '../app/layout/PageLayout';
import useAppSelector from '../app/redux/useAppSelector';
import LoadingIndicator from '../components/LoadingIndicator';
import type { DocumentType } from '../graphql/generated/gql';
import { gql } from '../graphql/generated/gql';
import type { SearchViewExternalResultsQuery } from '../graphql/generated/graphql';
import { SEARCH_PLATFORM_INFO_MAP } from '../integrations/IntegrationPlatformInfo';
import { parseSearchQuery } from '../topbar/quillFacets';
import { useIsSize } from '../utils/responsive/isSize';
import Sizes from '../utils/responsive/Sizes';
import { isAiQuestion } from '../utils/search/aiAsk';

const ENCOURAGE_INSTALLATION_PLATFORMS = ['google-drive', 'slack'];

// eslint-disable-next-line import/no-unused-modules -- exported for testing
export const SLAB_SEARCH_TYPES_QUERY = gql(/** @complexity 2 */ `
  query SearchViewSlabSearchTypes(
    $query: String!
    $filter: SearchFilter
  ) {
    searchTypes(
      query: $query
      filter: $filter
    ) {
      type
      count
    }
  }
`);

const KEYWORD_EXTRACTION_QUERY = gql(/** @complexity 1 */ `
  query SearchViewKeywordExtraction($text: String!) {
    keywords: aiKeywords(text: $text)
  }
`);

const EXTERNAL_SEARCH_QUERY = gql(/** @complexity 68 */ `
  query SearchViewExternalResults($platform: String!, $query: String!) {
    externalSearch(platform: $platform, query: $query) {
      id
      ...SearchViewExternalResults_mention
    }
  }
`);

const USE_EXTERNAL_SEARCH_QUERY_PLATFORM_FRAGMENT = gql(`
  fragment useExternalSearchQuery_platform on ExternalSearchPlatform {
    platform
    personalAuthorized
    teamAuthorized
  }
`);

const useExternalSearchQuery = ({
  searchQuery,
  platform,
  type,
  skip,
}: {
  searchQuery: string;
  platform?: DocumentType<typeof USE_EXTERNAL_SEARCH_QUERY_PLATFORM_FRAGMENT>;
  type?: string;
  skip: boolean;
}) => {
  const {
    // eslint-disable-next-line @typescript-eslint/naming-convention -- Add boolean prefix next time file is edited
    personalAuthorized,
    platform: platformKey = '',
    // eslint-disable-next-line @typescript-eslint/naming-convention -- Add boolean prefix next time file is edited
    teamAuthorized,
  } = platform ?? {};

  const [externalResults, setExternalResults] = useState<
    Record<
      string,
      Pick<
        QueryResult<
          SearchViewExternalResultsQuery,
          { platform: string; query: string }
        >,
        'data' | 'error'
      >
    >
  >({});

  const platformInfo = SEARCH_PLATFORM_INFO_MAP[platformKey];
  const isAuthorized = personalAuthorized || teamAuthorized;

  const [loadExternalSearch] = useLazyQuery(EXTERNAL_SEARCH_QUERY);

  const loadPlatformData = async (
    query: string,
    externalPlatformKey: string,
  ) => {
    const res = await loadExternalSearch({
      variables: { query, platform: externalPlatformKey },
      fetchPolicy: 'network-only',
    });

    // This will work around the issue where the error field is not set in the lazy query executor result
    // https://github.com/apollographql/apollo-client/issues/12097#issuecomment-2523542147
    const error =
      res.error ??
      (res.errors ? new ApolloError({ graphQLErrors: res.errors }) : undefined);

    setExternalResults((d) => ({
      ...d,
      [externalPlatformKey]: { data: res.data, error },
    }));
    return { count: res.data?.externalSearch?.length ?? null };
  };

  const [previousSearchQuery, setPreviousSearchQuery] = useState(searchQuery);
  if (searchQuery !== previousSearchQuery) {
    setPreviousSearchQuery(searchQuery);
    setExternalResults({});
  }

  const { data, error } = externalResults[platformKey] ?? {};

  const results =
    platformInfo && isAuthorized ? (data?.externalSearch ?? []) : [];
  const filteredResults = _.compact(filterBySearchResultType(type, results));

  return {
    results,
    filteredResults,
    error,
    loading: !!platformKey && !externalResults[platformKey] && !skip,
    loadPlatformData,
  };
};

// eslint-disable-next-line import/no-unused-modules -- exported for testing
export const EXTERNAL_PLATFORMS_QUERY = gql(/** @complexity 2 */ `
  query SearchViewExternalPlatforms($redirectPath: String!) {
    externalSearchPlatforms(redirectPath: $redirectPath) {
      ...useExternalSearchQuery_platform
      ...SearchManagePlatformButton_platforms
      ...SearchViewHeader_platforms
      ...SearchViewExternalResults_platform
    }
  }
`);

const useExternalSearchPlatforms = () => {
  const userType = useAppSelector((state) => state.session.user.type);

  const location = useLocation();
  const [searchParams] = useSearchParams();
  const q = searchParams.get('q');

  const params = new URLSearchParams({
    platform: '$PLATFORM',
  });

  if (q != null) {
    params.set('q', q);
  }

  const redirectPath = `${location.pathname}?${params.toString()}`;

  const { data, loading, refetch } = useQuery(EXTERNAL_PLATFORMS_QUERY, {
    fetchPolicy: 'network-only',
    variables: { redirectPath },
  });

  const externalSearchPlatforms = data?.externalSearchPlatforms;

  return useMemo(() => {
    const results = externalSearchPlatforms?.map((platform) => ({
      ...platform,
      teamAuthorized: userType !== 'GUEST' ? platform.teamAuthorized : false,
    }));

    return { loading, data: results, refetch };
  }, [externalSearchPlatforms, loading, refetch, userType]);
};

const useEncourageInstallationSet = (
  platforms?: { personalAuthorized?: boolean; teamAuthorized?: boolean }[],
) => {
  const dismissedSearchConnectionPrompts = useAppSelector(
    (state) => state.ui.dismissedSearchConnectionPrompts,
  );

  return useMemo(() => {
    if (
      !platforms ||
      platforms.some((p) => p.personalAuthorized || p.teamAuthorized)
    ) {
      return new Set([]);
    }

    return new Set(
      _.without(
        ENCOURAGE_INSTALLATION_PLATFORMS,
        ...dismissedSearchConnectionPrompts,
      ),
    );
  }, [dismissedSearchConnectionPrompts, platforms]);
};

type AiAskSource = {
  id: string;
  primary: string;
  snippet: string;
};

export type AiAskStreamResult = {
  threadId: string;
  answer: string;
  sources: AiAskSource[];
};

const useAiAskStream = ({
  query,
  disabled,
}: {
  query: string;
  disabled: boolean;
}) => {
  const socket = useAppSelector((state) => state.session.socket);
  const channelRef = useRef<Channel | null>(null);
  const [value, setValue] = useState<{
    loading: boolean;
    response: AiAskStreamResult | null;
  }>({ loading: false, response: null });

  const getOrSetupChannel = useCallback(() => {
    if (!socket || channelRef.current) {
      return channelRef.current;
    }

    channelRef.current = socket.channel('stream');
    attachAiAsk(channelRef.current, setValue);
    channelRef.current.join();
    return channelRef.current;
  }, [setValue, socket]);

  useEffect(() => {
    if (disabled) {
      setValue({ loading: false, response: null });
      return;
    }
    const channel = getOrSetupChannel();
    if (!channel) {
      setValue({ loading: false, response: null });
      return;
    }
    setValue({ loading: true, response: null });
    channel.push('ai_ask', { question: query });
  }, [disabled, query, getOrSetupChannel]);

  useEffect(
    () => () => {
      channelRef.current?.leave();
      channelRef.current = null;
    },
    [],
  );

  return value;
};

const useAiAsk = ({
  searchInput,
  askMode,
  disabled,
}: {
  searchInput: string;
  askMode: boolean;
  disabled: boolean;
}) => {
  const isQuestion = isAiQuestion(searchInput);

  const { response, loading } = useAiAskStream({
    query: searchInput,
    disabled: disabled || !askMode || !isQuestion,
  });

  const [previousSearchInput, setPreviousSearchInput] = useState(searchInput);
  if (searchInput !== previousSearchInput) {
    setPreviousSearchInput(searchInput);
  }

  return {
    aiAskData: response,
    isAiAskLoading: loading,
  };
};

interface SearchViewProps {
  initialPlatform?: string;
  initialType?: string;
}

const SearchView = ({ initialPlatform, initialType }: SearchViewProps) => {
  const [, startTransition] = useTransition();
  const isDesktop = useIsSize(Sizes.LG);
  const [tab, setTab] = useState({
    platform: initialPlatform,
    type: initialType,
  });

  const setPlatform = (key?: string) => {
    if (key === tab.platform) {
      return;
    }

    startTransition(() => {
      setTab({ platform: key, type: undefined });
    });
  };

  const setType = (key?: string) => {
    if (key === tab.type) {
      return;
    }

    startTransition(() => {
      setTab((currentTab) => ({ platform: currentTab.platform, type: key }));
    });
  };

  const [searchParams] = useSearchParams();
  const mode = searchParams.get('mode');
  const searchInput = searchParams.get('q') ?? '';
  const authError = searchParams.get('authError') ?? '';

  const isAskMode = mode === 'ask';
  const isAiAskEnabled = useIsAiAskEnabled();

  const platformKey =
    tab.platform && SEARCH_PLATFORM_INFO_MAP[tab.platform]?.platform;

  const parsedVariables = useMemo(
    () => parseSearchQuery(searchInput),
    [searchInput],
  );

  const keywordResponse = useQuery(KEYWORD_EXTRACTION_QUERY, {
    fetchPolicy: 'network-only',
    variables: { text: parsedVariables.query },
    skip: !isAskMode || !isAiAskEnabled,
  });

  const { keywords, skip, variables } = useMemo(() => {
    if (keywordResponse.loading) {
      return {
        skip: true,
        variables: parsedVariables,
        keywords: undefined,
      };
    }

    const joinedKeywords = keywordResponse.data?.keywords?.join(', ');
    const query = joinedKeywords || parsedVariables.query;

    return {
      skip: !query && Object.keys(parsedVariables.filter ?? {}).length === 0,
      variables: { ...parsedVariables, query },
      keywords: joinedKeywords,
    };
  }, [keywordResponse, parsedVariables]);

  const { aiAskData, isAiAskLoading } = useAiAsk({
    searchInput: parsedVariables.query,
    askMode: isAskMode,
    disabled: !isAiAskEnabled || !!tab.type || !!platformKey,
  });

  useEffect(() => {
    if (mode === 'ask') {
      setTab({ platform: undefined, type: undefined });
    }
  }, [searchInput, mode]);

  const { data: searchTypesData, loading: isSearchTypesQueryLoading } =
    useQuery(SLAB_SEARCH_TYPES_QUERY, {
      variables,
      skip,
    });
  const searchTypes = searchTypesData?.searchTypes ?? null;

  const { data: platforms, refetch: refetchPlatforms } =
    useExternalSearchPlatforms();
  const platform = platforms?.find((p) => p.platform === platformKey);
  const encouragedPlatformSet = useEncourageInstallationSet(platforms);

  const externalResponse = useExternalSearchQuery({
    searchQuery: variables.query,
    platform,
    type: tab.type,
    skip,
  });

  const externalCountsByType = _.countBy(
    externalResponse.results,
    getSearchResultType,
  );

  const countsByType: React.ComponentProps<
    typeof SearchViewHeader
  >['countsByType'] =
    searchTypes?.reduce((acc, searchType) => {
      return {
        ...acc,
        [searchType.type.toLowerCase()]: searchType.count,
      };
    }, {}) ?? {};

  // In case we are on a tab that has no results,
  // we need to reset the tab, so that we can show all results instead
  const shouldResetTab =
    !isSearchTypesQueryLoading &&
    platformKey == null &&
    tab.type != null &&
    countsByType[tab.type] == null;

  if (shouldResetTab) {
    setTab({ platform: undefined, type: undefined });
  }

  return (
    <PageLayout.Content noPaddingX>
      <div
        className={classNames(styles.container, {
          [styles.showSidebar]: isDesktop,
        })}
      >
        <div className={styles.title}>
          {(aiAskData || isAiAskLoading) && (
            <div className={styles.aiAnswer}>
              <AiAskAnswer query={parsedVariables.query} ask={aiAskData} />
            </div>
          )}
          <div className={styles.headerContainer}>
            <div
              className={classNames(styles.headerTitleContainer, {
                [styles.noKeywords]: !keywords,
              })}
            >
              <div className={styles.headerTitle}>
                <h2>Search results</h2>
                {(keywordResponse.loading || !skip) && (
                  <div className={styles.loading}>
                    <LoadingIndicator delay={0} adaptColor />
                  </div>
                )}
              </div>
              {isDesktop ? (
                <SearchViewFilterMenu />
              ) : (
                <SearchManagePlatformButton
                  platforms={platforms || []}
                  refetch={refetchPlatforms}
                />
              )}
            </div>
            {keywords && (
              <div className={styles.keywords}>Keywords: {keywords}</div>
            )}
          </div>
        </div>
        <div className={styles.header}>
          <SearchViewHeader
            activePlatformKey={platformKey}
            encouragedPlatformSet={encouragedPlatformSet}
            platforms={platforms || []}
            type={tab.type}
            setType={setType}
            setPlatform={setPlatform}
            externalCountsByType={externalCountsByType}
            countsByType={countsByType}
            vertical={isDesktop}
            verticalExtra={
              isDesktop ? (
                <div className={styles.verticalExtraContainer}>
                  <SearchManagePlatformButton
                    platforms={platforms || []}
                    refetch={refetchPlatforms}
                  />
                </div>
              ) : null
            }
            query={variables.query}
            skip={skip}
            loadPlatformData={externalResponse.loadPlatformData}
          />
        </div>
        <div className={styles.results}>
          {platform && (
            <SearchViewExternalResults
              authError={authError}
              encouragedPlatformSet={encouragedPlatformSet}
              platform={platform}
              results={externalResponse.filteredResults}
              loading={externalResponse.loading}
              error={externalResponse.error}
            />
          )}
          {!keywordResponse.loading && !skip && (
            <>
              <SearchViewSlabSearch
                variables={variables}
                active={!tab.platform && tab.type == null}
              />
              {Object.keys(countsByType).map((type) => (
                <SearchViewSlabSearch
                  key={type}
                  type={type}
                  variables={variables}
                  active={!tab.platform && type === tab.type}
                />
              ))}
            </>
          )}
        </div>
      </div>
    </PageLayout.Content>
  );
};

export default SearchView;
